// Import necessary hooks and components from React, Material UI, and React Router
//import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { ThemeProvider, CssBaseline, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SnackbarProvider, closeSnackbar } from "notistack";
import { createTheme } from "@mui/material/styles";
import { themeSettings } from "theme";
import { useMemo, lazy, Suspense, useEffect } from "react";
import { StaleBuildBoundary, StaleBuildNotice } from "components/StaleBuildBoundary";
import {
  unstable_HistoryRouter as HistoryRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { createBrowserHistory } from "history";
import PrivateRoute from "components/PrivateRoute";
import PlatformAdminRoute from "components/PlatformAdminRoute";
import ForgotUsername from "scenes/forgotusername";
import ForgotPassword from "scenes/forgotpassword";
import ResetPassword from "scenes/resetpassword";
import AccountSettings from "scenes/accountsettings";
import { fetchSubscription } from "state/actions/subscriptionActions";
import { bootstrapAuth } from "state/actions/authActions";
import { fetchWorkspaces, resetWorkspaces } from "state/actions/workspaceActions";

// Single app-wide history instance. Using HistoryRouter (instead of BrowserRouter)
// with the standalone `history` package gives us a navigator that exposes block(),
// which the unsaved-changes guard on the New Project page relies on to intercept
// in-app navigation. BrowserRouter's internal navigator has no block().
const appHistory = createBrowserHistory({ window });

// Lazy loading components to optimize initial load time
const Layout = lazy(() => import("scenes/layout"));
const AdminAuditPage = lazy(() => import("scenes/admin/AdminAuditPage"));
const AdminBillingPage = lazy(() => import("scenes/admin/AdminBillingPage"));
const AdminPage = lazy(() => import("scenes/admin"));
const AdminOrganizationsPage = lazy(() => import("scenes/admin/AdminOrganizationsPage"));
const AdminProjectsPage = lazy(() => import("scenes/admin/AdminProjectsPage"));
const AdminUsersPage = lazy(() => import("scenes/admin/AdminUsersPage"));
const MainPage = lazy(() => import("scenes/mainpage"));
const Login = lazy(() => import("scenes/login"));
const Register = lazy(() => import("scenes/register"));
const SsoCallback = lazy(() => import("scenes/ssocallback"));
const Terms = lazy(() => import("scenes/legal/terms"));
const VerifyAccount = lazy(() => import("scenes/verify"));
const MarketingOptIn = lazy(() => import("scenes/marketingoptin"));
const OrganizationInvite = lazy(() => import("scenes/organizationinvite"));
const Projects = lazy(() => import("scenes/projects"));
const ProjectView = lazy(() => import("scenes/projectview"));
const NewProject = lazy(() => import("scenes/newproject"));
const GeometryGenerator = lazy(() =>
  import("scenes/tools/geometrygenerator"),
);
const EnergyPostProcessing = lazy(() =>
  import("scenes/tools/energypostprocessing"),
);
const WeatherGenerator = lazy(() =>
  import("scenes/tools/weathergenerator"),
);
const PaymentSuccess = lazy(() => import("scenes/plan/paymentsuccess"));
const PaymentCancel = lazy(() => import("scenes/plan/paymentcancel"));
const UserGuide = lazy(() => import("scenes/help/UserGuide"));
const ReleaseNotes = lazy(() => import("scenes/help/ReleaseNotes"));
const SampleFiles = lazy(() => import("scenes/help/SampleFiles"));

const NotFound = () => {
  return <div>404 - Not Found</div>;
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated, checked } = useSelector((s) => s.auth);
  const mode = useSelector((s) => s.mode.mode);
  const selectedWorkspaceKey = useSelector(
    (s) => s.workspace.selectedWorkspaceKey,
  );
  const validatedMode = ["light", "dark"].includes(mode) ? mode : "light";
  const theme = useMemo(
    () => createTheme(themeSettings(validatedMode)),
    [validatedMode],
  );
  const isPersonalWorkspace =
    typeof selectedWorkspaceKey !== "string" ||
    !selectedWorkspaceKey.startsWith("org:");

  // 1) Bootstrap auth once on mount
  useEffect(() => {
    dispatch(bootstrapAuth());
  }, [dispatch]);

  // 2) After auth is known, fetch subscription for logged-in users
  useEffect(() => {
    if (checked && isAuthenticated) {
      dispatch(fetchSubscription());
      dispatch(fetchWorkspaces());
      return;
    }

    if (checked && !isAuthenticated) {
      dispatch(resetWorkspaces());
    }
  }, [dispatch, checked, isAuthenticated]);

  // 3) Force external links to open in a new tab
  useEffect(() => {
    const links = document.querySelectorAll("a[href^='http']");
    links.forEach((link) => {
      // Skip if same hostname (internal link)
      if (!link.href.includes(window.location.hostname)) {
        link.setAttribute("target", "_blank");
        link.setAttribute("rel", "noopener noreferrer");
      }
    });
  }, []);

  // Gate rendering until auth has been checked to avoid flicker/false redirects
  if (!checked) return <div>Loading...</div>;

  return (
    <div className="app">
      <HistoryRouter history={appHistory}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {/* App-wide toasts. Anchored to the VIEWPORT, so a result is visible wherever the
              user is scrolled — the page-top banners these replace were routinely off-screen
              when the action that triggered them was near the bottom of a long page.
              Errors persist until dismissed; everything else auto-hides. */}
          <SnackbarProvider
            maxSnack={3}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            autoHideDuration={5000}
            action={(snackbarId) => (
              <IconButton
                size="small"
                aria-label="Dismiss message"
                onClick={() => closeSnackbar(snackbarId)}
                sx={{ color: "inherit" }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          >
          {/* A code-split chunk that 404s after a deploy used to unmount the whole route to a
              blank page. The boundary turns that into "a new version is available"; the
              notice covers the runtime `await import(...)` failures no boundary can see. */}
          <StaleBuildNotice />
          <StaleBuildBoundary>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes>
              <Route path="/" element={<MainPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-username" element={<ForgotUsername />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/reset-password/:token"
                element={<ResetPassword />}
              />
              <Route path="/register" element={<Register />} />
              <Route path="/auth/sso/:provider/callback" element={<SsoCallback />} />
              <Route path="/legal/terms" element={<Terms />} />
              <Route path="/verify" element={<VerifyAccount />} />
              <Route path="/marketing-optin" element={<MarketingOptIn />} />
              <Route path="/organization-invite" element={<OrganizationInvite />} />
              <Route
                path="/projects/:projectId"
                element={
                  <PrivateRoute>
                    <ProjectView />
                  </PrivateRoute>
                }
              />
              <Route element={<Layout />}>
                <Route
                  path="/dashboard"
                  element={
                    <PrivateRoute>
                      <Navigate to="/projects" replace />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <AccountSettings />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/help/user-guide"
                  element={
                    <PrivateRoute>
                      <UserGuide />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/help/release-notes"
                  element={
                    <PrivateRoute>
                      <ReleaseNotes />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/help/sample-files"
                  element={
                    <PrivateRoute>
                      <SampleFiles />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <PrivateRoute>
                      <PlatformAdminRoute>
                        <AdminPage />
                      </PlatformAdminRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/audit"
                  element={
                    <PrivateRoute>
                      <PlatformAdminRoute>
                        <AdminAuditPage />
                      </PlatformAdminRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/billing"
                  element={
                    <PrivateRoute>
                      <PlatformAdminRoute>
                        <AdminBillingPage />
                      </PlatformAdminRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <PrivateRoute>
                      <PlatformAdminRoute>
                        <AdminUsersPage />
                      </PlatformAdminRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/organizations"
                  element={
                    <PrivateRoute>
                      <PlatformAdminRoute>
                        <AdminOrganizationsPage />
                      </PlatformAdminRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/admin/projects"
                  element={
                    <PrivateRoute>
                      <PlatformAdminRoute>
                        <AdminProjectsPage />
                      </PlatformAdminRoute>
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <PrivateRoute>
                      <Projects />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/newproject"
                  element={
                    <PrivateRoute>
                      <NewProject />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/plan"
                  element={
                    <PrivateRoute>
                      <Navigate
                        to={
                          isPersonalWorkspace
                            ? "/settings?tab=workspace"
                            : "/settings?tab=organizations"
                        }
                        replace
                      />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tools/geometry-generator"
                  element={
                    <PrivateRoute>
                      <GeometryGenerator />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tools/energy-postprocessing"
                  element={
                    <PrivateRoute>
                      <EnergyPostProcessing />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/tools/weather-generator"
                  element={
                    <PrivateRoute>
                      <WeatherGenerator />
                    </PrivateRoute>
                  }
                />
              </Route>
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/payment-cancel" element={<PaymentCancel />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </StaleBuildBoundary>
          </SnackbarProvider>
        </ThemeProvider>
      </HistoryRouter>
    </div>
  );
}

export default App;
